import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Stack
} from '@mui/material';
import {
  Add,
  Delete,
  Translate,
  CheckCircle,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import {
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';

/**
 * GlossaryTermForm Component
 * 
 * Allows creation of glossary terms with:
 * - English text (mandatory - reference language)
 * - Source language terms (translations FROM other languages TO English)
 * 
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close handler
 * @param {function} onSaved - Success callback
 * @param {number} glossaryId - ID of the glossary to add term to
 */
const GlossaryTermForm = ({ open, onClose, onSaved, glossaryId }) => {
  // Reference data
  const [languages, setLanguages] = useState([]);
  const [englishLanguage, setEnglishLanguage] = useState(null);

  // Core term form data (English is the reference)
  const [formData, setFormData] = useState({
    english_text: '',
    term_type: 'word'
  });

  // Source language translations (FROM other languages TO English)
  const [sourceTranslations, setSourceTranslations] = useState({});

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedTranslations, setExpandedTranslations] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});

  // Load initial data
  useEffect(() => {
    if (open) {
      loadLanguages();
      resetForm();
    }
  }, [open]);



  /**
   * Load active languages from API
   */
  const loadLanguages = async () => {
    try {
      const response = await axios.get('/terminology/languages/');
      const activeLanguages = (response.data.results || response.data || [])
        .filter(lang => lang.is_active);
      setLanguages(activeLanguages);
      
      // Find and set English language
      const english = activeLanguages.find(lang => lang.code === 'en');
      setEnglishLanguage(english);
    } catch (err) {
      console.error('Error loading languages:', err);
      setError('Failed to load languages');
    }
  };

  /**
   * Reset form to initial state
   */
  const resetForm = () => {
    setFormData({
      english_text: '',
      term_type: 'word'
    });
    setSourceTranslations({});
    setError('');
    setSuccess('');
    setValidationErrors({});
  };

  /**
   * Get languages available for source translations
   * Excludes English (since it's the reference language)
   */
  const getAvailableSourceLanguages = () => {
    return languages.filter(lang => lang.code !== 'en');
  };

  /**
   * Validate form data
   * @returns {boolean} - Whether form is valid
   */
  const validateForm = () => {
    const errors = {};

    if (!formData.english_text.trim()) {
      errors.english_text = 'English text is required';
    }

    if (!englishLanguage) {
      errors.general = 'English language not found in system';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Build translations array for API submission
   * English is the source, other languages are translations
   * @param {number} termId - ID of the created glossary term
   * @returns {Array} - Array of translation objects
   */
  const buildTranslationsPayload = (termId) => {
    const translations = [];

    // Add source language translations (FROM other languages TO English)
    Object.entries(sourceTranslations).forEach(([langId, text]) => {
      if (text && text.trim()) {
        translations.push({
          glossary_term: termId,
          language_id: parseInt(langId),
          translated_text: text.trim()
        });
      }
    });

    return translations;
  };

  /**
   * Submit form - create term in English and translations
   */
  const handleSubmit = async () => {
    // Validate
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Step 1: Create the glossary term (English as source)
      const termPayload = {
        glossary: glossaryId,
        source_text: formData.english_text.trim(),
        source_language_id: englishLanguage.id,
        term_type: formData.term_type
      };

      const termResponse = await axios.post('/terminology/glossary-terms/', termPayload);
      const createdTerm = termResponse.data;

      // Step 2: Create translations (from other languages TO English)
      const translationsPayload = buildTranslationsPayload(createdTerm.id);
      
      if (translationsPayload.length > 0) {
        // Create each translation
        const translationPromises = translationsPayload.map(translation =>
          axios.post('/terminology/glossary-term-translations/', translation)
        );
        
        await Promise.all(translationPromises);
      }

      // Success!
      setSuccess(`Successfully created term "${formData.english_text}" with ${translationsPayload.length} translation(s)`);
      
      // Notify parent
      if (onSaved) {
        setTimeout(() => {
          onSaved();
          onClose();
        }, 1500);
      }

    } catch (err) {
      console.error('Error creating term:', err);
      const errorMessage = err.response?.data?.detail 
        || err.response?.data?.message
        || err.response?.data?.source_text?.[0]
        || err.response?.data?.english_text?.[0]
        || 'Failed to create glossary term';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle source translation text change
   */
  const handleTranslationChange = (langId, value) => {
    setSourceTranslations(prev => ({
      ...prev,
      [langId]: value
    }));
  };

  /**
   * Clear translation for a language
   */
  const clearTranslation = (langId) => {
    const newTranslations = { ...sourceTranslations };
    delete newTranslations[langId];
    setSourceTranslations(newTranslations);
  };

  /**
   * Get count of filled source translations
   */
  const getFilledTranslationsCount = () => {
    return Object.values(sourceTranslations).filter(t => t && t.trim()).length;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Translate color="primary" />
          <Typography variant="h6">Create Glossary Term</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
            {success}
          </Alert>
        )}

        {/* SECTION 1: Core Term Information */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3,
            mb: 3,
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3,
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '1.125rem'
            }}
          >
            English Term (Reference Language)
          </Typography>
          
          <Stack spacing={3}>
            {/* Term Type */}
            <Box>
              <Typography 
                variant="body2" 
                sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}
              >
                Term Type
              </Typography>
              <ToggleButtonGroup
                value={formData.term_type}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setFormData({ ...formData, term_type: newValue });
                  }
                }}
                fullWidth
                color="primary"
              >
                <ToggleButton value="word" sx={{ py: 1.5 }}>
                  Word
                </ToggleButton>
                <ToggleButton value="acronym" sx={{ py: 1.5 }}>
                  Acronym
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* English Text */}
            <TextField
              fullWidth
              required
              multiline
              minRows={2}
              maxRows={6}
              label="English Text"
              value={formData.english_text}
              onChange={(e) => setFormData({ ...formData, english_text: e.target.value })}
              error={!!validationErrors.english_text}
              helperText={validationErrors.english_text || "The term or phrase in English (reference language)"}
              autoFocus
            />
          </Stack>
        </Paper>

        {/* SECTION 2: Source Language Translations */}
        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="space-between"
            sx={{ cursor: 'pointer' }}
            onClick={() => setExpandedTranslations(!expandedTranslations)}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                Source Language Translations
              </Typography>
              <Chip 
                label={`${getFilledTranslationsCount()} of ${getAvailableSourceLanguages().length}`}
                size="small"
                color={getFilledTranslationsCount() > 0 ? "success" : "default"}
              />
            </Box>
            <IconButton size="small">
              {expandedTranslations ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={expandedTranslations}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Provide the same term in other languages (translations FROM source languages TO English)
              </Typography>

              {getAvailableSourceLanguages().length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No source languages available for translation.
                </Alert>
              ) : (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  {getAvailableSourceLanguages().map((lang) => (
                    <Paper 
                      key={lang.id}
                      variant="outlined" 
                      sx={{ 
                        p: 2,
                        bgcolor: sourceTranslations[lang.id] ? 'success.50' : 'background.paper'
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {lang.name} ({lang.code.toUpperCase()})
                        </Typography>
                        {lang.name_native && lang.name_native !== lang.name && (
                          <Typography variant="caption" color="text.secondary">
                            - {lang.name_native}
                          </Typography>
                        )}
                        {sourceTranslations[lang.id] && (
                          <IconButton 
                            size="small" 
                            onClick={() => clearTranslation(lang.id)}
                            sx={{ ml: 'auto' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder={`Term in ${lang.name}...`}
                        value={sourceTranslations[lang.id] || ''}
                        onChange={(e) => handleTranslationChange(lang.id, e.target.value)}
                      />
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Collapse>
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading || !formData.english_text}
          startIcon={loading ? <CircularProgress size={20} /> : <Add />}
        >
          {loading ? 'Creating...' : 'Create Term'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GlossaryTermForm;
